// Generate a printable packing slip for a sales order / invoice
import { groupCartByRoom, splitRoomLabel, UNASSIGNED_ROOM } from './cartGroups';

const esc = (v) => String(v ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const label = (v) => v ? String(v).replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '—';

export const openPackingSlip = (doc, docType = 'Sales Order') => {
  const items = doc.items || [];
  const groups = groupCartByRoom(items);
  const ref = (doc.so_number || doc.invoice_number || doc.id?.slice(0, 8) || 'N/A').toString().toUpperCase();
  const customer = doc.customer_name || '';
  const email = doc.customer_email || '';
  const phone = doc.customer_phone || doc.phone || '';
  const date = new Date(doc.created_at || Date.now()).toLocaleDateString();

  // Addresses
  const ship = [doc.shipping_address, doc.shipping_city, doc.shipping_state, doc.shipping_zip].filter(Boolean);
  const bill = [doc.billing_address, doc.billing_city, doc.billing_state, doc.billing_zip].filter(Boolean);
  const shipAddr = ship.length ? ship.join(', ') : bill.join(', ');

  const totalQty = items.reduce((s, i) => s + (i.quantity || 1), 0);

  let rowsHtml = '';
  let rowNum = 0;
  groups.forEach(g => {
    g.items.forEach(item => {
      rowNum++;
      const config = item.config || item;
      const { window: win } = splitRoomLabel(config.room_label || '');
      const product = label(config.product_type || 'roller-shades');
      const fabric = config.fabric_color_name || config.fabric_color || '—';
      const w = config.width || '—';
      const h = config.height || '—';
      const qty = item.quantity || 1;
      rowsHtml += `<tr>
        <td class="num">${rowNum}</td>
        <td class="room">${esc(g.room)}</td>
        <td>${esc(win || '—')}</td>
        <td>${esc(product)}</td>
        <td class="fabric">${esc(fabric)}</td>
        <td class="size">${w}" x ${h}"</td>
        <td class="qty">${qty}</td>
        <td class="check"><span class="box"></span></td>
      </tr>`;
    });
  });

  const html = `<!DOCTYPE html><html><head>
    <title>Packing Slip — ${esc(ref)}</title>
    <style>
      @page { size: portrait; margin: 0.5in; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #171717; padding: 20px; }
      .header { display: flex; justify-content: space-between; border-bottom: 3px solid #171717; padding-bottom: 12px; margin-bottom: 16px; }
      .header h1 { font-size: 22px; letter-spacing: 1px; }
      .header .sub { font-size: 11px; color: #666; }
      .addresses { display: flex; gap: 40px; margin-bottom: 20px; }
      .addr-block { flex: 1; }
      .addr-block h3 { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #888; border-bottom: 1px solid #ddd; padding-bottom: 4px; margin-bottom: 6px; }
      .addr-block p { font-size: 12px; line-height: 1.5; }
      .meta-row { display: flex; gap: 24px; margin-bottom: 16px; font-size: 11px; }
      .meta-row .chip { background: #f5f5f4; border: 1px solid #e7e5e4; border-radius: 6px; padding: 6px 12px; }
      .meta-row .chip strong { display: block; font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; color: #888; margin-bottom: 2px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
      th { background: #fafaf9; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #555; padding: 6px 8px; border-bottom: 2px solid #d6d3d1; text-align: left; white-space: nowrap; }
      td { padding: 7px 8px; border-bottom: 1px solid #e7e5e4; }
      .num { width: 28px; text-align: center; font-weight: 600; color: #999; }
      .room { font-weight: 600; }
      .fabric { max-width: 120px; word-break: break-word; }
      .size { white-space: nowrap; font-family: monospace; }
      .qty { text-align: center; font-weight: 600; }
      .check { text-align: center; width: 40px; }
      .box { display: inline-block; width: 16px; height: 16px; border: 2px solid #555; border-radius: 2px; }
      .total-row { font-weight: 700; background: #fafaf9; }
      .notes-area { border: 1px dashed #ccc; border-radius: 8px; padding: 12px; margin-top: 16px; min-height: 80px; }
      .notes-area h4 { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #888; margin-bottom: 8px; }
      .footer { margin-top: 24px; text-align: center; font-size: 10px; color: #aaa; border-top: 1px solid #ddd; padding-top: 8px; }
      @media print { body { padding: 0; } }
    </style>
  </head><body>
    <div class="header">
      <div>
        <h1>APOLLO SHADES</h1>
        <div class="sub">Packing Slip</div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:16px;font-weight:700;">#${esc(ref)}</div>
        <div style="font-size:11px;color:#666;">${date}</div>
      </div>
    </div>

    <div class="addresses">
      <div class="addr-block">
        <h3>Ship To</h3>
        <p><strong>${esc(customer)}</strong></p>
        ${shipAddr ? `<p>${esc(shipAddr)}</p>` : ''}
        ${phone ? `<p>${esc(phone)}</p>` : ''}
        ${email ? `<p style="color:#888;">${esc(email)}</p>` : ''}
      </div>
      <div class="addr-block">
        <h3>From</h3>
        <p><strong>Apollo Shades</strong></p>
        <p>1 Brooklyn Navy Yard, Building 33, Suite 201B</p>
        <p>Brooklyn, NY 11205</p>
        <p>(212) 209-0654</p>
      </div>
    </div>

    <div class="meta-row">
      <div class="chip"><strong>Total Items</strong>${totalQty}</div>
      <div class="chip"><strong>Rooms</strong>${groups.length}</div>
      <div class="chip"><strong>Fulfillment</strong>${label(doc.fulfillment_type || 'shipping')}</div>
    </div>

    <table>
      <thead><tr>
        <th>#</th>
        <th>Room</th>
        <th>Window</th>
        <th>Product</th>
        <th>Fabric / Color</th>
        <th>Size (W x H)</th>
        <th>Qty</th>
        <th>Packed</th>
      </tr></thead>
      <tbody>
        ${rowsHtml}
        <tr class="total-row">
          <td colspan="6" style="text-align:right;">Total</td>
          <td class="qty">${totalQty}</td>
          <td></td>
        </tr>
      </tbody>
    </table>

    <div class="notes-area">
      <h4>Packing Notes</h4>
    </div>

    <div class="footer">Apollo Shades — 1 Brooklyn Navy Yard, Building 33, Suite 201B, Brooklyn, NY 11205 — (212) 209-0654 — Printed ${new Date().toLocaleDateString()}</div>
  </body></html>`;

  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 400);
};
